import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';

import {
  PDP_SELECTION_VALIDITY_STATES,
  PDP_DECIDELATER_TAB_ID,
  PDP_CUSTOMIZE_TAB_ID,
  PDP_READYTOSHIP_TAB_ID,
} from 'commons/constants';

import useCart from 'data-hooks/useCart';
import usePDPFabricFilters from 'data-hooks/usePDPFabricFilters';
import useProductFavoriteV2 from 'data-hooks/useProductFavoriteV2';
import useUserCartEvent from 'data-hooks/useEvents/useUserCartEvent';
import useModularDetails from 'data-hooks/useModularDetails';
import useModularPDPCart from 'data-hooks/useModularPDPCart';
import { getCombinedOptionSKUs } from 'data-hooks/useProductLocalStock/utils';
import SuspenseWithLoader from 'components/consumer/SuspenseWithLoader';
import useIsZipCodeFromUserServiceable from 'global-state/isZipCodeFromUserServiceable/useIsZipCodeFromUserServiceable';
import usePDPBasicDetails from 'components/consumer/PDP/state/context/usePDPBasicDetails';
import usePDPPrice from 'data-hooks/usePDPPrice';
import PDPAddToBagSectionUI from './PDPAddToBagSectionUI';
import usePdpIsModularBYOProduct from '../state/context/usePdpIsModularBYOProduct';
import usePdpSelectedCustomizerTab from '../state/pdpSelectedCustomizerTab/usePdpSelectedCustomizerTab';
import usePdpSelectedProtectionPlanId from '../state/pdpSelectedProtectionPlanId/usePdpSelectedProtectionPlanId';
import usePDPOptions from '../state/context/usePDPOptions';
import usePDPStockAvailability from '../state/context/usePDPStockAvailability';
import PDPAddToBagSectionPlaceholder from './PDPAddToBagSectionPlaceholder';

const PDPAddToBagSectionInner = ({
  mode = 'pdp',
  onAddToCartClicked,
  pageLoading,
  productId = null,
  slug = null,
  stickyDesktopButtonOnly = false,
  isEditOrder = false,
}) => {
  const [addToCartError, setAddToCartError] = useState(null);
  const [buildYourOwnDetails, updateBuildYourOwnDetails] = useState({});

  const { isFavorited } = useProductFavoriteV2();
  const { data: priceData } = usePDPPrice();
  const isZipCodeFromUserServiceable = useIsZipCodeFromUserServiceable();

  const {
    loading: basicDetailsLoading,
    data: basicDetails,
  } = usePDPBasicDetails();

  const {
    loading: modularDetailsLoading,
    data: modularDetailsData,
  } = useModularDetails(slug);

  const {
    data: { addingToCart: modularAddingToCart, modularTotalPrice },
    actions: { addModularToCart },
  } = useModularPDPCart(productId, slug);

  const { loading: optionsLoading, data: optionData } = usePDPOptions();

  const {
    loading: availabilityLoading,
    data: {
      isEFGInternal,
      isMarkedOOSOrDiscontinued,
      isOutOfStock,
      isDiscontinued,
    },
  } = usePDPStockAvailability();

  const {
    data: { addingToCart },
    actions: { addToCart },
  } = useCart();

  const pdpSelectedCustomizerTab = usePdpSelectedCustomizerTab();

  const {
    data: { selectedMaterial },
  } = usePDPFabricFilters(slug, isEditOrder);

  const { isClearance, clearanceStockPerSKU } = priceData || {};
  const isDecideLaterTab = pdpSelectedCustomizerTab === PDP_DECIDELATER_TAB_ID;
  const isRTSTab = pdpSelectedCustomizerTab === PDP_READYTOSHIP_TAB_ID;

  // cart events
  const { addToCartEvent } = useUserCartEvent(
    basicDetails,
    optionData,
    priceData,
    isClearance,
    isFavorited
  );

  const pdpIsModularBYOProduct = usePdpIsModularBYOProduct();
  const pdpSelectedProtectionPlanId = usePdpSelectedProtectionPlanId();

  const onAddToCart = async () => {
    if (pdpIsModularBYOProduct) {
      addModularToCart({
        decideLater: pdpSelectedCustomizerTab === PDP_DECIDELATER_TAB_ID,
      });
      return;
    }

    setAddToCartError(null);
    const { errors, cart, cartToken } = await addToCart({
      productId: basicDetails.id,
      selectedOptions: optionData.selectedOptions,
      decideLater: pdpSelectedCustomizerTab === PDP_DECIDELATER_TAB_ID,
      selectedProtectionPlanId: pdpSelectedProtectionPlanId,
    });

    if (errors) {
      setAddToCartError(
        errors?.[0]?.message || 'Unable to add to cart. Please try again.'
      );
      return;
    }

    onAddToCartClicked?.();
    addToCartEvent(cart, cartToken, mode);
  };

  useEffect(() => {
    if (isZipCodeFromUserServiceable) {
      setAddToCartError(null);
    }
  }, [isZipCodeFromUserServiceable]);

  const allowUserToCustomize = useMemo(() => {
    if (availabilityLoading || optionsLoading || modularDetailsLoading) {
      return false;
    }

    if (
      isEFGInternal ||
      isEmpty(optionData?.options) ||
      optionData?.pdpHasUserSelectedOptions ||
      !optionData?.upholsteryOptionId ||
      pdpSelectedCustomizerTab !== PDP_CUSTOMIZE_TAB_ID ||
      optionData?.options.find(
        optValue => optValue.id === optionData?.upholsteryOptionId
      )?.values?.length === 1
    ) {
      return false;
    }

    return true;
  }, [
    availabilityLoading,
    isEFGInternal,
    optionsLoading,
    optionData,
    modularDetailsLoading,
    pdpSelectedCustomizerTab,
  ]);

  const validStockOptionSkus = useMemo(() => {
    if (!!optionData.stock?.stockOptionsCombined?.length && isRTSTab) {
      const updatedDisabledState = getCombinedOptionSKUs(
        optionData.selectedOptionsVariantSku,
        optionData.stock?.stockOptionsCombined
      );
      return updatedDisabledState;
    }
    return [];
  }, [optionData.stock, optionData.selectedOptionsVariantSku, isRTSTab]);

  const isCartDisabled = useMemo(() => {
    if (availabilityLoading || optionsLoading) {
      return false;
    }
    if (validStockOptionSkus?.length && optionData?.selectedOptionsVariantSku) {
      if (
        !validStockOptionSkus.includes(optionData?.selectedOptionsVariantSku)
      ) {
        return true;
      }
    }
    if (!optionData?.stock?.stockOptions?.length || isOutOfStock) {
      return isOutOfStock;
    }
    const { selectionValidity } = optionData.stock;
    return [
      PDP_SELECTION_VALIDITY_STATES.INVALID,
      PDP_SELECTION_VALIDITY_STATES.INACTIVE,
    ].includes(selectionValidity);
  }, [
    availabilityLoading,
    optionsLoading,
    optionData.stock,
    optionData.selectedOptionsVariantSku,
    isOutOfStock,
    validStockOptionSkus,
  ]);

  useEffect(() => {
    if (
      !modularDetailsData?.isModularBundleProduct ||
      !modularDetailsData?.preConfigProducts?.length
    ) {
      return;
    }
    const updatedProducts = modularDetailsData.preConfigProducts.filter(
      prod => !!prod && prod.id !== basicDetails?.id
    );
    let finalSlug = basicDetails?.meta?.collectionSlug
      ? basicDetails?.meta?.collectionSlug.replace('collections/', '')
      : '';
    finalSlug = finalSlug.length
      ? `${finalSlug.charAt(0).toUpperCase()}${finalSlug.slice(1)}`
      : '';
    updateBuildYourOwnDetails({
      name: finalSlug,
      urlKey: `/modular/${finalSlug.toLowerCase()}-modular/`,
      dimensions: `Choose from ${updatedProducts.length} pieces to make any configuration.`,
    });
  }, [
    basicDetails?.id,
    basicDetails?.meta?.collectionSlug,
    modularDetailsData?.isModularBundleProduct,
    modularDetailsData.preConfigProducts,
  ]);

  const loading =
    basicDetailsLoading ||
    optionsLoading ||
    pageLoading ||
    modularDetailsLoading;

  return (
    <PDPAddToBagSectionUI
      addToCartError={addToCartError}
      addingToCart={pdpIsModularBYOProduct ? modularAddingToCart : addingToCart}
      allowUserToCustomize={allowUserToCustomize}
      buildYourOwnDetails={buildYourOwnDetails}
      cartDisabled={
        loading ||
        isCartDisabled ||
        (pdpIsModularBYOProduct && !modularTotalPrice) ||
        (isMarkedOOSOrDiscontinued && (isOutOfStock || isDiscontinued))
      }
      collectionSlug={basicDetails?.meta?.collectionSlug}
      isDiscontinued={isDiscontinued}
      isModularBundleProduct={modularDetailsData?.isModularBundleProduct}
      isOutOfStock={isOutOfStock}
      loading={loading}
      mode={mode}
      onAddToCart={!loading ? onAddToCart : undefined}
      price={basicDetails?.meta?.price?.final_price}
      showClearanceAlert={
        isClearance && !clearanceStockPerSKU?.flashSale && !isDecideLaterTab
      }
      stickyDesktopButtonOnly={stickyDesktopButtonOnly}
      campaign={basicDetails?.custom_properties?.campaign}
      selectedMaterial={selectedMaterial}
    />
  );
};

PDPAddToBagSectionInner.propTypes = {
  mode: PropTypes.oneOf(['quickview', 'pdp']),
  onAddToCartClicked: PropTypes.func,
  pageLoading: PropTypes.bool,
  productId: PropTypes.number,
  slug: PropTypes.string,
  stickyDesktopButtonOnly: PropTypes.bool,
  isEditOrder: PropTypes.bool,
};

const PDPAddToBagSection = props => (
  <SuspenseWithLoader fallback={<PDPAddToBagSectionPlaceholder />}>
    <PDPAddToBagSectionInner {...props} />
  </SuspenseWithLoader>
);

export default PDPAddToBagSection;
